import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// Deterministically stringify objects so query keys remain stable across renders.
const stableStringify = (value) =>
    JSON.stringify(value, (key, val) => {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            return Object.keys(val)
                .sort()
                .reduce((acc, k) => {
                    acc[k] = val[k]
                    return acc
                }, {})
        }
        return val
    })

// Remove undefined/null values (and empty arrays) so requests and cache keys stay clean.
const normalizeParameters = (params) => {
    if (!params || typeof params !== 'object') return {}

    const normalized = {}
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) return
        if (Array.isArray(value)) {
            const cleaned = value.filter((item) => item !== undefined && item !== null)
            if (cleaned.length) normalized[key] = cleaned
            return
        }
        normalized[key] = value
    })

    return normalized
}

// Resolve the origin for SSR (PWA_DOMAIN_SITE) while preserving client behavior.
const getFetchOrigin = (appOrigin) => {
    if (typeof window !== 'undefined') return appOrigin

    const pwaDomain = process.env.PWA_DOMAIN_SITE
    if (!pwaDomain) return appOrigin

    const protocol = pwaDomain.startsWith('localhost') ? 'http' : 'https'
    return `${protocol}://${pwaDomain}`
}

// Build a querystring for GET requests (repeats keys for arrays).
const buildQueryString = (params) => {
    if (!params || typeof params !== 'object') return ''
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) return
        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item === undefined || item === null) return
                searchParams.append(key, String(item))
            })
            return
        }
        searchParams.append(key, String(value))
    })
    const query = searchParams.toString()
    return query ? `?${query}` : ''
}

// Call the SSR product search endpoint with normalized parameters.
const fetchProductSearch = async (origin, parameters) => {
    const response = await fetch(
        `${origin}/api/product-search-ssr${buildQueryString(parameters)}`,
        {
            method: 'GET'
        }
    )

    if (!response.ok) {
        let message = 'Failed to fetch product search results'
        try {
            const errorJson = await response.json()
            if (errorJson?.error) message = errorJson.error
        } catch (error) {
            // Ignore JSON parsing errors for non-JSON responses
        }
        throw new Error(message)
    }

    return response.json()
}

export const useProductSearchSSR = (apiOptions = {}, queryOptions = {}) => {
    const appOrigin = useAppOrigin()
    const {site} = useMultiSite()
    const origin = getFetchOrigin(appOrigin)
    const siteId = site?.id

    const parameters = useMemo(
        () => {
            const merged = {
                ...apiOptions?.parameters,
                siteId: apiOptions?.parameters?.siteId || siteId
            }
            return normalizeParameters(merged)
        },
        [apiOptions?.parameters, siteId]
    )

    const queryKey = useMemo(
        () => ['product-search', stableStringify(parameters)],
        [parameters]
    )

    return useQuery({
        ...queryOptions,
        queryKey,
        queryFn: () => fetchProductSearch(origin, parameters)
    })
}


